<template>
  <div v-if="showSyncBar" class="sync-status-bar">
    <div class="sync-content">
      <div class="sync-icon">
        <span class="sync-spinner">🔄</span>
      </div>
      <div class="sync-text">
        <h4>Synchronizing with Strava</h4>
        <p>{{ syncMessage }}</p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>
      <div class="sync-stats">
        <div class="stat-item">
          <span class="stat-label">Peaks:</span>
          <span class="stat-value">{{ animatedStats.peaks }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Activities:</span>
          <span class="stat-value">{{ animatedStats.activities }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Ascents:</span>
          <span class="stat-value">{{ animatedStats.ascents }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import api from '@/services/api'

const authStore = useAuthStore()
const dataStore = useDataStore()

const showSyncBar = computed(() => {
  const authenticated = authStore.isAuthenticated
  const stravaConnected = authStore.isStravaConnected
  const stravaSynced = authStore.isStravaSynced
  const processingComplete = isProcessingComplete.value

  console.log('SyncStatusBar conditions:', {
    authenticated,
    stravaConnected,
    stravaSynced,
    processingComplete,
    showBar: authenticated && stravaConnected && (!stravaSynced || !processingComplete)
  })

  // Simplified condition: show bar if connected to Strava and not fully processed
  return authenticated && stravaConnected && !processingComplete
})

const syncMessage = ref('Loading your activities...')
const progressPercent = ref(0)
const animatedStats = ref({
  peaks: 0,
  activities: 0,
  ascents: 0
})

const isProcessingComplete = ref(false)

const syncInterval = ref(null)
const userCheckInterval = ref(null)
const animationTimeouts = ref([])

// Animation function for counting up
const animateValue = (startVal, endVal, duration, callback) => {
  const startTime = Date.now()
  const difference = endVal - startVal

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)
    const currentValue = Math.floor(startVal + (difference * progress))

    callback(currentValue)

    if (progress < 1) {
      const timeout = setTimeout(animate, 16) // ~60fps
      animationTimeouts.value.push(timeout)
    }
  }

  animate()
}

// Update stats with animation
const updateStatsWithAnimation = (newStats) => {
  // Clear existing animation timeouts
  animationTimeouts.value.forEach(timeout => clearTimeout(timeout))
  animationTimeouts.value = []

  // Animate each stat
  animateValue(animatedStats.value.peaks, newStats.peaks, 1000, (value) => {
    animatedStats.value.peaks = value
  })

  animateValue(animatedStats.value.activities, newStats.activities, 1000, (value) => {
    animatedStats.value.activities = value
  })

  animateValue(animatedStats.value.ascents, newStats.ascents, 1000, (value) => {
    animatedStats.value.ascents = value
  })
}

// Load current data and animate
const loadAndAnimateStats = async () => {
  try {
    // Load fresh data from API (force refresh during sync)
    await dataStore.loadAscents(true)
    await dataStore.loadActivities(true)

    // Calculate current stats
    const currentStats = {
      peaks: dataStore.uniqueLocations,
      activities: dataStore.activities.length,
      ascents: dataStore.ascents.length
    }

    // Calculate progress based on ascents vs activities
    if (currentStats.activities > 0) {
      progressPercent.value = Math.min((currentStats.ascents / currentStats.activities) * 100, 95) // Max 95% until fully synced
    }

    // Animate to new values
    updateStatsWithAnimation(currentStats)

    // Update sync message based on progress
    if (currentStats.ascents > 0) {
      syncMessage.value = `Found ${currentStats.ascents} ascents on ${currentStats.peaks} peaks...`
    } else {
      syncMessage.value = 'Scanning your activities...'
    }

  } catch (error) {
    console.error('Error loading sync stats:', error)
    syncMessage.value = 'Connection issues, retrying...'
  }
}

// Check if sync is complete
const checkSyncStatus = async () => {
  try {
    await authStore.fetchUser()

    // First check if Strava connection is synced (activities downloaded)
    if (!authStore.isStravaSynced) {
      console.log('SyncStatusBar: Strava not synced yet, showing bar')
      return // Still syncing activities from Strava
    }

    // Then check if all activities are processed (worker finished)
    console.log('SyncStatusBar: Checking processing status...')
    const syncStatusResponse = await api.get('/api/user/sync_strava')
    const syncStatus = syncStatusResponse.data
    console.log('SyncStatusBar: Sync status response:', syncStatus)

    if (syncStatus.is_fully_synced) {
      // Full sync completed, stop intervals
      console.log('SyncStatusBar: Processing complete, hiding bar')
      isProcessingComplete.value = true
      progressPercent.value = 100
      stopSyncMonitoring()
    } else {
      // Update message to show processing status
      console.log('SyncStatusBar: Still processing, showing bar')
      isProcessingComplete.value = false
      syncMessage.value = `Processing ${syncStatus.unprocessed_activities} activities...`
    }
  } catch (error) {
    console.error('Error checking sync status:', error)
    syncMessage.value = 'Connection issues, retrying...'
  }
}

// Start monitoring sync progress
const startSyncMonitoring = () => {
  // Load initial data
  loadAndAnimateStats()

  // Set up periodic data loading (every 20 seconds)
  syncInterval.value = setInterval(loadAndAnimateStats, 20000)

  // Set up periodic sync status checking (every 30 seconds)
  userCheckInterval.value = setInterval(checkSyncStatus, 30000)
}

// Stop monitoring
const stopSyncMonitoring = () => {
  if (syncInterval.value) {
    clearInterval(syncInterval.value)
    syncInterval.value = null
  }

  if (userCheckInterval.value) {
    clearInterval(userCheckInterval.value)
    userCheckInterval.value = null
  }

  // Clear animation timeouts
  animationTimeouts.value.forEach(timeout => clearTimeout(timeout))
  animationTimeouts.value = []
}

// Watch for auth changes
watch(() => authStore.isAuthenticated, (newVal) => {
  if (newVal && authStore.isStravaConnected && !authStore.isStravaSynced) {
    startSyncMonitoring()
  } else {
    stopSyncMonitoring()
  }
})

watch(() => authStore.isStravaSynced, (newVal) => {
  if (newVal) {
    stopSyncMonitoring()
  }
})

onMounted(async () => {
  if (showSyncBar.value) {
    startSyncMonitoring()
  }
  
  // Always check sync status on mount to ensure proper state
  await checkSyncStatus()
})

onUnmounted(() => {
  stopSyncMonitoring()
})
</script>

<style scoped>
.sync-status-bar {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 0;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
}

.sync-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.sync-icon {
  display: flex;
  align-items: center;
}

.sync-spinner {
  font-size: 1.5rem;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.sync-text {
  flex: 1;
}

.sync-text h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.sync-text p {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 2px;
  transition: width 0.5s ease;
}

.sync-stats {
  display: flex;
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Responsive */
@media (max-width: 768px) {
  .sync-content {
    padding: 0.75rem 1rem;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .sync-stats {
    justify-content: center;
    gap: 1rem;
  }

  .stat-item {
    flex-direction: row;
    gap: 0.5rem;
  }

  .stat-label {
    margin-bottom: 0;
  }

  .stat-value {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .sync-stats {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .stat-item {
    flex: 1 1 calc(50% - 0.375rem);
    min-width: 80px;
  }
}
</style>